import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import PureHeading from '../../../common/Heading/PureHeading';
import BasicBanner from '../../base/BasicBanner/BasicBanner';
import FilterSearchBox from '../../base/FilterPanel/FilterSearchBox/FilterSearchBox';
import { DownloadsHistoryProps } from './DownloadsHistory.types';
import useDebouncedCallback from '../../../../lib/hooks/useDebouncedCallback';
import { DATE_FORMAT_FALLBACK, DEFAULT_DEBOUNCE_TIME } from '../../../../lib/constants/globals';
import styles from './DownloadsHistory.module.scss';
import { useScorm } from '../../../../lib/context/ScormContext';
import { useDownloadsHistory } from '../../../../lib/api/scormApi';
import { useViewContext } from '../../../../lib/context/ViewContext';
import useFeature from '../../../../lib/hooks/useFeature';
import LayoutContainer from '../../../common/Layout/Layout';
import { useDevice } from '../../../../lib/hooks/device';
import { mapDownloadItemToTableRow } from './DownloadHistory.utils';
import TableManager from '../../base/TableManager/TableManager';
import ContentBlockView from '../../../common/ContentBlockView/ContentBlockView';
import { useId } from '../../../../lib/hooks/useId';
import { useConfig } from '../../../../lib/hooks/config';
import { replacePlaceholder } from '../../../../lib/utils/placeholders/replacePlaceholder';
import { downloadsHistoryListIds } from '../../../../lib/tests/testIds';
import { Label } from '../../../common/Label/Label';
import { DOWNLOADS_HISTORY_LIST_HEADINGS } from '../../../../lib/constants/courses';
import { COMMON_LABELS, DOWNLOADS_LIST_LABELS } from '../../../../lib/constants/labelKeys';
import { LaptopIcon } from '../../../../lib/icons/LaptopIcon';
import { Api } from '../../../../lib/types/apiSpec';
import { FeatureToggleCodename } from '../../../../lib/types/enums';

const DownloadsHistory = ({
  heading,
  noDownloadHistoryItemsMessage,
  noSearchResultMessage,
  headingTemplateLabel,
  ...props
}: DownloadsHistoryProps) => {
  const { globalDateFormat = DATE_FORMAT_FALLBACK } = useConfig() ?? {};
  const viewContext = useViewContext();
  const device = useDevice();
  const downloadsListEnabled = useFeature(FeatureToggleCodename.scormDownloadsList);
  const ean = viewContext.app.user?.mainFacilityEan;
  const [searchValue, setSearchValue] = useState('');
  const { data, isValidating, isLoading } = useDownloadsHistory({
    ean,
    shouldFetch: !!downloadsListEnabled && !!viewContext?.app?.user,
  });
  const { addItem, actionsState, downloadsList } = useScorm();
  const tableIdBase = useId('download-history-');
  const isRevalidating = isValidating && !isLoading;

  const handleSearchChange = useDebouncedCallback((searchPhrase: string) => {
    setSearchValue(searchPhrase);
  }, DEFAULT_DEBOUNCE_TIME);

  const handleAddItem = ({
    id,
    trainingName,
    trainingType,
  }: {
    id: string;
    trainingName: string;
    trainingType: Api.TrainingType | null;
  }) => {
    if (trainingType) {
      addItem({ id, trainingName, trainingType });
    }
  };

  const tableData = useMemo(() => {
    return data?.map(item => ({
      ...item,
      items: item.items
        .filter(row => !searchValue || row.name.toLowerCase().includes(searchValue.toLowerCase()))
        .reduce(
          mapDownloadItemToTableRow(['name'], { onClickHandler: handleAddItem, downloadsList, actionsState }),
          {},
        ),
    }));
  }, [data, device, actionsState, downloadsList.data, searchValue]);

  const isNoSearchResult =
    searchValue && !!noSearchResultMessage && tableData?.every(item => !Object.keys(item.items).length);

  const isNoHistoryItemsData = !isLoading && (!tableData || !tableData.length);

  return (
    <div>
      <BasicBanner
        variant="primaryBoostedBlue"
        background={isNoHistoryItemsData ? undefined : { color: 'SecondaryPurple', variant: 'Half' }}
      >
        <PureHeading level={HeadingLevel.h2} as={HeadingLevel.h1}>
          {heading}
        </PureHeading>
      </BasicBanner>
      {isNoHistoryItemsData ? (
        !!noDownloadHistoryItemsMessage && (
          <div className={styles.noResultsWrapper}>
            <LayoutContainer>
              <div
                role="status"
                className={styles.noResultsContentWrapper}
                data-testid={downloadsHistoryListIds.noHistoryItemsMessage}
              >
                <ContentBlockView contentBlock={noDownloadHistoryItemsMessage} />
              </div>
            </LayoutContainer>
            <LaptopIcon className={styles.noResultsIcon} />
          </div>
        )
      ) : (
        <>
          <div className={styles.searchContainer}>
            <div className={styles.innerSearchContainer}>
              <FilterSearchBox
                value={searchValue}
                onChange={handleSearchChange}
                className={styles.searchBox}
                customPlaceholderKey={COMMON_LABELS.SEARCH.DOWNLOADS_HISTORY}
              />
            </div>
          </div>
          <LayoutContainer>
            {isNoSearchResult ? (
              <div
                role="status"
                className={styles.noResultsContentWrapper}
                data-testid={downloadsHistoryListIds.noSearchResultItemsMessage}
              >
                <ContentBlockView contentBlock={noSearchResultMessage} />
              </div>
            ) : (
              tableData?.map(
                table =>
                  !!Object.keys(table.items).length && (
                    <div key={table.id} className={styles.tableWrapper}>
                      <PureHeading level={HeadingLevel.h3} className={styles.tableHeading}>
                        <span>
                          {replacePlaceholder(
                            { ...table, downloadDate: dayjs(table.downloadDate).format(globalDateFormat) },
                            headingTemplateLabel ?? '',
                          )}
                        </span>
                        <Label name={DOWNLOADS_LIST_LABELS.formatLabel(table.learningStandard)} />
                      </PureHeading>
                      <TableManager
                        {...props}
                        id={`${tableIdBase}-${table.id}`}
                        mobileVariant="secondary"
                        isInitialLoad={isLoading}
                        isLoading={isRevalidating}
                        data={table.items || {}}
                        className={styles.tableContainer}
                        customClasses={{ headContainer: styles.tableHeadingContainer }}
                        headings={DOWNLOADS_HISTORY_LIST_HEADINGS}
                      />
                    </div>
                  ),
              )
            )}
          </LayoutContainer>
        </>
      )}
    </div>
  );
};

export default DownloadsHistory;
